import React, { useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Container,
  Button,
  Box,
  IconButton,
  useScrollTrigger,
  Slide,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import { useAuth } from '../context/AuthContext';

const navItems = [
  { name: 'Home', path: '/' },
  { name: 'Services', path: '/services' },
  { name: 'Tax Tools', path: '/tax-tools' },
  { name: 'Insights', path: '/resources' },
  { name: 'About Us', path: '/about' },
  { name: 'Contact', path: '/contact' },
];

const Navbar: React.FC = () => {
  const { isAuthenticated, role } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const trigger = useScrollTrigger();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center', p: 2 }}>
      <Box
        component="img"
        src="/logo.svg"
        alt="Fingard Logo"
        sx={{ height: 50, my: 2 }}
      />
      <List>
        {navItems.map((item) => (
          <ListItem key={item.name} disablePadding>
            <ListItemButton
              component={RouterLink}
              to={item.path}
              sx={{ textAlign: 'center', borderRadius: 2, my: 0.5 }}
            >
              <ListItemText primary={item.name} primaryTypographyProps={{ fontWeight: 600 }} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 3, px: 2 }}>
        <Button
          fullWidth
          variant="contained"
          component={RouterLink}
          to="/payment"
          sx={{ bgcolor: 'secondary.main', color: 'secondary.contrastText', fontWeight: 700 }}
        >
          Pay Online
        </Button>
        <Button
          fullWidth
          variant="outlined"
          component={RouterLink}
          to={isAuthenticated ? (role === 'sales' ? '/fp-sales-9k7m' : (role === 'employee' || role === 'admin' ? '/fp-desk-9k7m' : '/client-dashboard')) : '/login'}
          startIcon={isAuthenticated ? <AccountCircleOutlinedIcon /> : <LockOutlinedIcon />}
          sx={{ borderColor: '#123D6B', color: '#123D6B', fontWeight: 700 }}
        >
          {isAuthenticated ? (role === 'sales' ? 'Sales Portal' : (role === 'employee' ? 'Staff Workspace' : 'My Dashboard')) : 'Client Login'}
        </Button>
      </Box>
    </Box>
  );

  return (
    <>
      <Slide appear={false} direction="down" in={!trigger}>
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            color: 'text.primary',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
            <Toolbar disableGutters sx={{ minHeight: { xs: 64, md: 74 }, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box
                component={RouterLink}
                to="/"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  textDecoration: 'none',
                  color: 'inherit',
                }}
              >
                <Box
                  component="img"
                  src="/logo.svg"
                  alt="Fingard Logo"
                  sx={{ height: { xs: 52, md: 62 }, mr: { xs: 1, md: 3 } }}
                />
              </Box>

              {isMobile ? (
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{ ml: 2, color: 'text.primary' }}
                >
                  <MenuIcon />
                </IconButton>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { md: 1, lg: 2 } }}>
                  {navItems.map((item) => {
                    const isActive = location.pathname === item.path;
                    return (
                      <Button
                        key={item.name}
                        component={RouterLink}
                        to={item.path}
                        sx={{
                          color: isActive ? '#123D6B' : 'text.primary',
                          fontWeight: isActive ? 700 : 600,
                          fontSize: '0.95rem',
                          whiteSpace: 'nowrap',
                          px: { md: 1.2, lg: 1.8 },
                          py: 1,
                          position: 'relative',
                          textTransform: 'none',
                          borderRadius: 2,
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            bottom: 4,
                            left: '50%',
                            transform: isActive ? 'translateX(-50%) scaleX(1)' : 'translateX(-50%) scaleX(0)',
                            width: '60%',
                            height: 2.5,
                            bgcolor: '#C59B27',
                            borderRadius: 2,
                            transition: 'transform 0.2s ease-in-out'
                          },
                          '&:hover': {
                            color: '#123D6B',
                            backgroundColor: 'rgba(18, 61, 107, 0.04)',
                            '&::after': {
                              transform: 'translateX(-50%) scaleX(1)'
                            }
                          },
                        }}
                      >
                        {item.name}
                      </Button>
                    );
                  })}

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: { md: 1, lg: 2 } }}>
                    <Button
                      variant="contained"
                      component={RouterLink}
                      to="/payment"
                      sx={{
                        bgcolor: 'secondary.main',
                        color: 'secondary.contrastText',
                        fontWeight: 700,
                        fontSize: '0.9rem',
                        px: 2.2,
                        py: 0.9,
                        borderRadius: 2,
                        whiteSpace: 'nowrap',
                        boxShadow: 'none',
                        textTransform: 'none',
                        '&:hover': { bgcolor: 'secondary.dark', boxShadow: '0 2px 8px rgba(197, 155, 39, 0.3)' }
                      }}
                    >
                      Pay Online
                    </Button>

                    {isAuthenticated ? (
                      <Button
                        variant="contained"
                        component={RouterLink}
                        to={role === 'sales' ? '/fp-sales-9k7m' : (role === 'employee' || role === 'admin' ? '/fp-desk-9k7m' : '/client-dashboard')}
                        startIcon={<AccountCircleOutlinedIcon />}
                        sx={{
                          bgcolor: '#123D6B',
                          color: '#FFFFFF',
                          fontWeight: 700,
                          fontSize: '0.9rem',
                          px: 2.2,
                          py: 0.9,
                          borderRadius: 2,
                          whiteSpace: 'nowrap',
                          boxShadow: 'none',
                          textTransform: 'none',
                          border: '1px solid rgba(197, 155, 39, 0.4)',
                          '&:hover': { bgcolor: '#0B2847', boxShadow: '0 2px 8px rgba(11, 40, 71, 0.3)' }
                        }}
                      >
                        {role === 'sales' ? 'Sales Portal' : (role === 'employee' ? 'Staff Workspace' : 'My Dashboard')}
                      </Button>
                    ) : (
                      <Button
                        variant="outlined"
                        component={RouterLink}
                        to="/login"
                        startIcon={<LockOutlinedIcon />}
                        sx={{
                          borderColor: '#123D6B',
                          color: '#123D6B',
                          fontWeight: 700,
                          fontSize: '0.9rem',
                          px: 2.2,
                          py: 0.85,
                          borderRadius: 2,
                          whiteSpace: 'nowrap',
                          textTransform: 'none',
                          borderWidth: 1.5,
                          '&:hover': { borderColor: '#0B2847', bgcolor: 'rgba(18, 61, 107, 0.04)', borderWidth: 1.5 }
                        }}
                      >
                        Client Login
                      </Button>
                    )}
                  </Box>
                </Box>
              )}
            </Toolbar>
          </Container>
        </AppBar>
      </Slide>

      <Box component="nav">
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: 240,
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>
    </>
  );
};

export default Navbar;
